import { ColDef } from 'ag-grid-community';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { exportTypeEnum, permissionLevelEnum } from '../../../../constants';
import { useDownloadCsvMutation } from '../../../../hooks/mutations/useDownloadMutation';
import { useAuth } from '../../../../hooks/useAuth';
import { DataItem } from '../../../../services/apiSLTP/dailyReports/activities';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { formatDate, formatDateToDDMMYYYY } from '../../../../utils/dates';
import ExportCsvIcon from '../../../ui/ExportCsvIcon';
import GenericAgGridTable from '../../GenericAgGridTable';

interface SubContractorsTableProps {
    activities: DataItem[];
    currentDate: Date;
    stepMode: number;
}

const SubContractorsTable: React.FC<SubContractorsTableProps> = ({ activities, currentDate, stepMode }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { mutate: exportCsv, isLoading: isExportCsvLoading } = useDownloadCsvMutation();

    const rows = useMemo(() => {
        const dailySubContractors = activities
            .map((activity) =>
                activity.dailySubContractors.map((dsc) => ({
                    ...dsc,
                    activityId: activity._id,
                    siteName: activity.siteId.name,
                    reportUserFirstName: activity.reportId.userId.firstName,
                    reportUserLastName: activity.reportId.userId.lastName,
                    subContractorTypeDesignation: dsc.subContractorTypeId.designation,
                    subContractorTypeCodeProC: dsc.subContractorTypeId.codeProC,
                }))
            )
            .flat()
            .sort((a, b) => {
                if (a.subContractorTypeDesignation > b.subContractorTypeDesignation) return 1;
                if (a.subContractorTypeDesignation < b.subContractorTypeDesignation) return -1;
                return 0;
            });

        return dailySubContractors;
    }, [activities]);

    type RowData = (typeof rows)[number];

    const columnDefs: ColDef<RowData>[] = [
        {
            headerName: capitalizeFirstLetter(t('date')),
            colId: 'date',
            valueGetter: () => formatDateToDDMMYYYY(currentDate, '/'),
            initialHide: true,
        },
        {
            headerName: capitalizeFirstLetter(t('company')),
            field: 'subContractorTypeDesignation',
            colId: 'company',
            initialFlex: 1,
            cellClass: 'pos-sy left-0 z-idx-4',
        },
        {
            headerName: capitalizeFirstLetter(t('codeProC')),
            field: 'subContractorTypeCodeProC',
            colId: 'codeProC',
            width: 120,
        },
        {
            headerName: capitalizeFirstLetter(t('site')),
            field: 'siteName',
            colId: 'site',
        },
        {
            headerName: capitalizeFirstLetter(t('reportOf')),
            colId: 'reportOf',
            valueGetter: (params) => formatName(params.data?.reportUserFirstName, params.data?.reportUserLastName),
        },
        ...((user?.profileId.permsLevel === permissionLevelEnum.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      colId: '_id',
                      initialWidth: 250,
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                  },
              ]
            : []) as ColDef<RowData>[]),
    ];

    return (
        <>
            <GenericAgGridTable
                rowData={rows}
                columnDefs={columnDefs}
                exportCsvFileName={`supervision-subcontractors-${formatDate(currentDate)}`}
                headerRightComponent={
                    <>
                        <ExportCsvIcon
                            initDate={currentDate}
                            onExport={(startDate, endDate, fileName, delimiter) =>
                                exportCsv({
                                    exportType: exportTypeEnum.SUBCONTRACTORS,
                                    queries: { startDate, endDate, delimiter, fileName },
                                })
                            }
                            exportIsLoading={isExportCsvLoading}
                            subTitle={capitalizeFirstLetter(t('tools'))}
                        />
                    </>
                }
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />
        </>
    );
};

export default SubContractorsTable;
